import { ServerEventValidationSchema } from '@thedealersconcierge/lib/cache/events';
import { QueryClient } from '@tanstack/react-query';
import { transactionKeys } from './keys/transaction';

const assertNever = (eventType: never): never => {
  throw new Error(`Unhandled server event type: ${JSON.stringify(eventType)}`);
};

/**
 * Map a realtime event to consumer cache invalidations.
 *
 * The customer only ever receives events for their own transactions (the channel
 * is per-customer), so we invalidate by the transaction's public id — the
 * trailing dealership slug in the key is matched by prefix.
 *
 * Invalidation only *refetches* queries that are currently active (have a
 * mounted observer — i.e. on screen): that is React Query's default
 * `refetchType: 'active'`, and we pass no override. So an event for one of the
 * customer's other transactions that is NOT in view never triggers a background
 * fetch — it is a no-op when the query is uncached, or just marks a
 * cached-but-inactive query stale so it refetches the next time it mounts.
 *
 * The `switch` is exhaustive (`assertNever`): adding a variant to the shared
 * schema is a compile error until its invalidation is wired here.
 */
export const applyServerEvent = (
  queryClient: QueryClient,
  event: ServerEventValidationSchema
): void => {
  switch (event.type) {
    case 'transaction.updated':
      void queryClient.invalidateQueries({
        queryKey: transactionKeys.all(event.transactionId)
      });
      return;
    default:
      return assertNever(event.type);
  }
};
